/**
 * Data Actions
 * Redux actions for fetching and managing application data
 */

import * as dashboardService from '../../services/dashboardService';
import * as walletService from '../../services/walletService';
import * as tradeService from '../../services/tradeService';
import * as referralService from '../../services/referralService';
import * as kycService from '../../services/kycService';
import * as paymentService from '../../services/paymentService';
import * as bankAccountService from '../../services/bankAccountService';
import * as marketDataService from '../../services/marketDataService';
import * as popupService from '../../services/popupService';

/**
 * Fetch dashboard data
 */
export const fetchDashboard = () => async (dispatch) => {
  dispatch({ type: 'FETCH_DASHBOARD_START' });
  try {
    const data = await dashboardService.getDashboard();
    dispatch({ type: 'FETCH_DASHBOARD_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    dispatch({ type: 'FETCH_DASHBOARD_FAILURE', payload: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Fetch wallet balance
 */
export const fetchWalletBalance = () => async (dispatch) => {
  try {
    const data = await walletService.getWalletBalance();
    dispatch({ type: 'FETCH_WALLET_BALANCE_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch wallet transactions
 */
export const fetchWalletTransactions = () => async (dispatch) => {
  try {
    const data = await walletService.getWalletTransactions();
    dispatch({ type: 'FETCH_WALLET_TRANSACTIONS_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch all trades
 */
export const fetchAllTrades = () => async (dispatch) => {
  dispatch({ type: 'FETCH_TRADES_START' });
  try {
    const data = await tradeService.getAllTrades();
    dispatch({ type: 'FETCH_ALL_TRADES_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch active trades
 */
export const fetchActiveTrades = () => async (dispatch) => {
  try {
    const data = await tradeService.getActiveTrades();
    dispatch({ type: 'FETCH_ACTIVE_TRADES_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch trade history
 */
export const fetchTradeHistory = () => async (dispatch) => {
  try {
    const data = await tradeService.getTradeHistory();
    dispatch({ type: 'FETCH_TRADE_HISTORY_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch trade stats
 */
export const fetchTradeStats = () => async (dispatch) => {
  try {
    const data = await tradeService.getTradeStats();
    dispatch({ type: 'FETCH_TRADE_STATS_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch forex trades
 * @param {Object} params - Query parameters (status, page, limit, sortBy, sortOrder)
 */
export const fetchForexTrades = (params = {}) => async (dispatch) => {
  dispatch({ type: 'FETCH_FOREX_TRADES_START' });
  try {
    const response = await tradeService.getForexTrades(params);
    const data = response.data || response;
    dispatch({ type: 'FETCH_FOREX_TRADES_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    dispatch({ type: 'FETCH_FOREX_TRADES_FAILURE', payload: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Fetch crypto trades
 * @param {Object} params - Query parameters (status, page, limit, sortBy, sortOrder)
 */
export const fetchCryptoTrades = (params = {}) => async (dispatch) => {
  dispatch({ type: 'FETCH_CRYPTO_TRADES_START' });
  try {
    const response = await tradeService.getCryptoTrades(params);
    const data = response.data || response;
    dispatch({ type: 'FETCH_CRYPTO_TRADES_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    dispatch({ type: 'FETCH_CRYPTO_TRADES_FAILURE', payload: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Fetch gold trades
 * @param {Object} params - Query parameters (status, page, limit, sortBy, sortOrder)
 */
export const fetchGoldTrades = (params = {}) => async (dispatch) => {
  dispatch({ type: 'FETCH_GOLD_TRADES_START' });
  try {
    const response = await tradeService.getGoldTrades(params);
    const data = response.data || response;
    dispatch({ type: 'FETCH_GOLD_TRADES_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    dispatch({ type: 'FETCH_GOLD_TRADES_FAILURE', payload: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Fetch referrals
 */
export const fetchReferrals = () => async (dispatch) => {
  try {
    const response = await referralService.getReferrals();
    // Extract data from response: { success: true, data: {...} }
    const referralsData = response.data || response;
    dispatch({ type: 'FETCH_REFERRALS_SUCCESS', payload: referralsData });
    return { success: true, data: referralsData };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch KYC status
 */
export const fetchKYC = () => async (dispatch) => {
  dispatch({ type: 'FETCH_KYC_START' });
  try {
    const response = await kycService.getKYCStatus();
    // Extract data from response: { success: true, data: { status, details, ... } }
    // Store the inner data object: { status, details, submittedAt, updatedAt, remarks }
    const kycData = response.data || response;
    dispatch({ type: 'FETCH_KYC_SUCCESS', payload: kycData });
    return { success: true, data: kycData };
  } catch (error) {
    // 404 means KYC hasn't been submitted yet - this is not an error
    if (error.status === 404 || (error.data && error.data.message && error.data.message.includes('No KYC details found'))) {
      // Set KYC to null to indicate it hasn't been submitted
      dispatch({ type: 'FETCH_KYC_SUCCESS', payload: null });
      return { success: true, data: null };
    }
    // For other errors, still dispatch failure
    dispatch({ type: 'FETCH_KYC_FAILURE', payload: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Submit KYC documents
 */
export const submitKYC = (formData) => async (dispatch) => {
  dispatch({ type: 'SUBMIT_KYC_START' });
  try {
    const response = await kycService.submitKYC(formData);
    // Extract data from response: { success: true, data: { status, details, ... } }
    const kycData = response.data || response;
    dispatch({ type: 'FETCH_KYC_SUCCESS', payload: kycData });
    return { success: true, data: kycData };
  } catch (error) {
    const errorMessage = error.message || (error.data && error.data.message) || 'Failed to submit KYC';
    dispatch({ type: 'SUBMIT_KYC_FAILURE', payload: errorMessage });
    return { success: false, error: errorMessage };
  }
};

/**
 * Update KYC documents
 */
export const updateKYC = (formData) => async (dispatch) => {
  dispatch({ type: 'UPDATE_KYC_START' });
  try {
    const response = await kycService.updateKYC(formData);
    // Extract data from response: { success: true, data: { status, details, ... } }
    const kycData = response.data || response;
    dispatch({ type: 'FETCH_KYC_SUCCESS', payload: kycData });
    return { success: true, data: kycData };
  } catch (error) {
    const errorMessage = error.message || (error.data && error.data.message) || 'Failed to update KYC';
    dispatch({ type: 'UPDATE_KYC_FAILURE', payload: errorMessage });
    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch KYC details (detailed information including documents)
 */
export const fetchKYCDetails = () => async (dispatch) => {
  dispatch({ type: 'FETCH_KYC_DETAILS_START' });
  try {
    const response = await kycService.getKYCDetails();
    // Extract data from response: { success: true, data: { kyc: {...}, userVerification: {...} } }
    const detailsData = response.data || response;
    dispatch({ type: 'FETCH_KYC_DETAILS_SUCCESS', payload: detailsData });
    return { success: true, data: detailsData };
  } catch (error) {
    const errorMessage = error.message || (error.data && error.data.message) || 'Failed to fetch KYC details';
    dispatch({ type: 'FETCH_KYC_DETAILS_FAILURE', payload: errorMessage });
    return { success: false, error: errorMessage };
  }
};

/**
 * Fetch payment methods
 */
export const fetchPaymentMethods = () => async (dispatch) => {
  try {
    const data = await paymentService.getPaymentMethods();
    dispatch({ type: 'FETCH_PAYMENT_METHODS_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch bank accounts
 */
export const fetchBankAccounts = () => async (dispatch) => {
  dispatch({ type: 'FETCH_BANK_ACCOUNTS_START' });
  try {
    const response = await bankAccountService.getBankAccounts();
    // Extract data from response: { success: true, data: [...] }
    const bankAccounts = response.data || response;
    dispatch({ type: 'FETCH_BANK_ACCOUNTS_SUCCESS', payload: Array.isArray(bankAccounts) ? bankAccounts : [] });
    return { success: true, data: bankAccounts };
  } catch (error) {
    dispatch({ type: 'FETCH_BANK_ACCOUNTS_FAILURE', payload: error.message });
    return { success: false, error: error.message };
  }
};

/**
 * Add bank account
 */
export const addBankAccount = (accountData) => async (dispatch) => {
  dispatch({ type: 'ADD_BANK_ACCOUNT_START' });
  try {
    const response = await bankAccountService.addBankAccount(accountData);
    // Extract data from response: { success: true, data: {...} }
    const bankAccount = response.data || response;
    dispatch({ type: 'ADD_BANK_ACCOUNT_SUCCESS', payload: bankAccount });
    // Refresh bank accounts list
    dispatch(fetchBankAccounts());
    return { success: true, data: bankAccount };
  } catch (error) {
    const errorMessage = error.data?.message || error.message || 'Failed to add bank account';
    dispatch({ type: 'ADD_BANK_ACCOUNT_FAILURE', payload: errorMessage });
    return { success: false, error: errorMessage, message: errorMessage };
  }
};

/**
 * Update bank account
 */
export const updateBankAccount = (accountId, accountData) => async (dispatch) => {
  dispatch({ type: 'UPDATE_BANK_ACCOUNT_START' });
  try {
    const response = await bankAccountService.updateBankAccount(accountId, accountData);
    // Extract data from response: { success: true, data: {...} }
    const bankAccount = response.data || response;
    dispatch({ type: 'UPDATE_BANK_ACCOUNT_SUCCESS', payload: bankAccount });
    // Refresh bank accounts list
    dispatch(fetchBankAccounts());
    return { success: true, data: bankAccount };
  } catch (error) {
    const errorMessage = error.data?.message || error.message || 'Failed to update bank account';
    dispatch({ type: 'UPDATE_BANK_ACCOUNT_FAILURE', payload: errorMessage });
    return { success: false, error: errorMessage, message: errorMessage };
  }
};

/**
 * Fetch transactions
 */
export const fetchTransactions = () => async (dispatch) => {
  try {
    const data = await paymentService.getTransactions();
    dispatch({ type: 'FETCH_TRANSACTIONS_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch symbols
 */
export const fetchSymbols = () => async (dispatch) => {
  try {
    const data = await marketDataService.getSymbols();
    dispatch({ type: 'FETCH_SYMBOLS_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Fetch popups
 */
export const fetchPopups = () => async (dispatch) => {
  try {
    const data = await popupService.getActivePopups();
    dispatch({ type: 'FETCH_POPUPS_SUCCESS', payload: data });
    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

